import {
  Box,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { useMemo } from 'react';

import { useCurrentRequest } from '../hooks/useCurrentRequest';
import { QuoteLineItem } from '../utils/types';
import { LabeledText } from './LabeledText';
import { MarkupDetail } from './QuoteCard/MarkupDetail';
import { RateDetail } from './QuoteCard/RateDetail';
import { SurchargeDetail } from './QuoteCard/SurchargeDetail';

type Props = {
  lineItem: QuoteLineItem;
};

export const ConnectionPopover = ({ lineItem }: Props) => {
  const { request } = useCurrentRequest();

  const hasConnection = useMemo(() => {
    const hasSurcharge = lineItem?.issued_by_surcharge_id !== null;
    const hasRate = lineItem?.issued_by_rate_id !== null;
    const hasTieredCost = lineItem?.issued_by_tiered_cost_id !== null;
    const hasMarkup = lineItem?.issued_by_markup_id !== null;

    return hasSurcharge || hasRate || hasTieredCost || hasMarkup;
  }, [
    lineItem?.issued_by_surcharge_id,
    lineItem?.issued_by_rate_id,
    lineItem?.issued_by_tiered_cost_id,
    lineItem?.issued_by_markup_id,
  ]);

  const connectionIsActive = useMemo(() => {
    return hasConnection && lineItem?.disassociated_at === null;
  }, [hasConnection, lineItem?.disassociated_at]);

  const requestCw = useMemo(() => request?.chargeable_weight, [request?.chargeable_weight]);

  const hasInvalidTieredCostSelected = useMemo(() => {
    return lineItem?.issued_by_rate?.costs?.some((cost) => {
      const max = cost?.max || Number.MAX_SAFE_INTEGER;
      const isPossible = (requestCw || 0) < max;
      const isSelected = cost.id === lineItem?.issued_by_tiered_cost_id;

      return isSelected && !isPossible;
    });
  }, [lineItem?.issued_by_rate?.costs, lineItem?.issued_by_tiered_cost_id, requestCw]);

  const connectionStatusColor = useMemo(() => {
    return hasConnection ? (hasInvalidTieredCostSelected ? 'error' : 'success') : 'muted';
  }, [hasConnection, hasInvalidTieredCostSelected]);

  return (
    <Popover isLazy>
      <PopoverTrigger>
        <Stack
          direction="row"
          align="center"
          justify="center"
          h="full"
          w="full"
          px={4}
          _hover={{ bgColor: 'whiteAlpha.100' }}
          cursor="pointer"
        >
          <Box
            w={2}
            h={2}
            borderRadius="full"
            bgColor={connectionIsActive ? connectionStatusColor : 'transparent'}
            borderWidth={1.5}
            borderColor={connectionStatusColor}
            opacity={hasConnection ? 1 : 0.4}
          />
        </Stack>
      </PopoverTrigger>
      <PopoverContent p="2">
        <Stack direction="column" spacing="2">
          <PopoverHeader borderBottomWidth={0}>
            <Stack direction="row" justify="space-between" align="center" spacing="4">
              <Text>Connection</Text>
              <PopoverCloseButton position="relative" top="0" right="0" />
            </Stack>
          </PopoverHeader>
          <PopoverBody borderRadius="md">
            <Stack direction="column" spacing="0" borderWidth={1} borderRadius="md" minW="200px">
              <Stack p={3} pb={0}>
                <Show if={!hasConnection}>
                  <LabeledText label="Status" value="No Connection" />
                </Show>
                <LabeledText label="Description" value={lineItem.description || '-'} />
              </Stack>
              <RateDetail lineItem={lineItem} />
              <SurchargeDetail lineItem={lineItem} />
              <MarkupDetail lineItem={lineItem} />
            </Stack>
          </PopoverBody>
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
